<template>
  <div v-if="user">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <UserAvatar
          :initials="getUserInitials(user)"
          :asset="user.picture"
          size="xlarge"
        />
        <div class="flex items-center gap-2">
          <h1 class="text-xl font-semibold text-gray-900">
            {{ user.name || "Unnamed User" }}
          </h1>
          <i
            v-if="user.is_admin"
            class="pi pi-shield text-green-600 text-lg"
            title="Admin"
          />
        </div>
      </div>

      <ActionsMenu
        v-if="showActions"
        :menu-items="actionMenuItems"
        :dangerous-action="dangerousAction"
      />
    </div>

    <Divider />

    <div class="space-y-3 text-sm">
      <div class="flex items-center">
        <div class="w-32 text-gray-600">Role:</div>
        <div class="flex flex-wrap gap-1">
          <Tag
            v-for="role in getUserRoles(user)"
            :key="role"
            :value="role"
            v-bind="getRoleTagProps(role)"
            class="text-xs"
          />
        </div>
      </div>

      <div class="flex items-start">
        <div class="w-32 text-gray-600">Email:</div>
        <div class="flex flex-col gap-0.5">
          <div class="text-gray-900">
            {{ user.email || user.shared_email || "N/A" }}
          </div>
          <!-- Who else is on this email. Replaces the old "Shared" badge: same
               fact, plus a count and a way to go look at them. -->
          <div v-if="sharedProfileCount > 0" class="text-xs text-gray-500">
            Shared account with
            <button
              type="button"
              class="text-primary hover:underline"
              data-testid="shared-profiles-link"
              @click="emit('viewSharedProfiles')"
            >
              {{ sharedProfileCount }}
              {{
                sharedProfileCount === 1 ? "other profile" : "other profiles"
              }}
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center">
        <div class="w-32 text-gray-600">Phone:</div>
        <div class="text-gray-900">
          {{ formatPhoneNumber(user.phone_number) || "N/A" }}
        </div>
      </div>

      <div class="flex items-center" data-testid="sms-consent-row">
        <div class="w-32 text-gray-600">Texting:</div>
        <div class="text-gray-900 flex items-center gap-3">
          <span>{{ smsConsent.text }}</span>
          <button
            v-if="smsConsent.optedOut"
            type="button"
            class="text-primary underline text-sm"
            data-testid="sms-consent-reset"
            @click="emit('resetSmsConsent')"
          >
            Record new consent
          </button>
        </div>
      </div>

      <div class="flex items-center">
        <div class="w-32 text-gray-600">Team:</div>
        <div
          v-if="user.teams && user.teams.length > 1"
          v-tooltip.top="getAllTeamNames(user)"
          class="text-gray-900 cursor-help"
        >
          {{ getTeamName(user) }}
        </div>
        <div v-else class="text-gray-900">{{ getTeamName(user) }}</div>
      </div>

      <div class="flex items-center">
        <div class="w-32 text-gray-600">Created:</div>
        <div class="text-gray-900">{{ getFormattedDate(user.created_at) }}</div>
      </div>

      <div class="flex items-center">
        <div class="w-32 text-gray-600">App Status:</div>
        <div class="flex items-center gap-2">
          <Tag
            :value="appStatus.label"
            :severity="appStatus.severity"
            class="text-xs"
          />
          <span
            v-if="
              user.mobile_app_status === 'invited' &&
              user.invited_to_mobile_app_at
            "
            class="text-gray-600"
          >
            {{ getInvitedDate(user.invited_to_mobile_app_at) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { AdminProfile } from "@/api/model";
import UserAvatar from "@/shared/components/UserAvatar.vue";
import ActionsMenu from "@/shared/components/ActionsMenu.vue";
import Tag from "primevue/tag";
import Divider from "primevue/divider";
import Tooltip from "primevue/tooltip";
import {
  getUserInitials,
  getUserRoles,
  getRoleTagProps,
  getTeamName,
  getAllTeamNames,
  getAppStatus,
  getInvitedDate,
} from "../helpers";
import { getFormattedDate } from "@/shared/utils/helpers";
import { formatPhoneNumber } from "@/shared/utils/phone-number";
import { smsConsentLabel } from "../helpers";
import { useBulkUserInvite } from "../composables/useBulkUserInvite";

const vTooltip = Tooltip;

interface Props {
  readonly user: AdminProfile;
  readonly showActions?: boolean;
  /** How many other profiles in this org use the same email (MAI-4171). */
  readonly sharedProfileCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  showActions: true,
  sharedProfileCount: 0,
});

const emit = defineEmits<{
  edit: [];
  setPassword: [];
  archive: [];
  userInvited: [];
  viewSharedProfiles: [];
  resetSmsConsent: [];
}>();

const { isInviting, inviteUsers } = useBulkUserInvite();

const showActions = computed(() => props.showActions);
const smsConsent = computed(() => smsConsentLabel(props.user));

const appStatus = computed(() => getAppStatus(props.user));

const handleInviteToMobileApp = async () => {
  const success = await inviteUsers([props.user]);
  if (success) {
    emit("userInvited");
  }
};

// Managed athletes (MAI-4171) have no login (user is null), so
// login-only actions don't apply to them
const hasLogin = computed(() => !!props.user.user);

const actionMenuItems = computed(() => [
  {
    label: "Edit",
    icon: "pi pi-pencil",
    command: () => emit("edit"),
  },
  ...(hasLogin.value
    ? [
        {
          label: "Set Password",
          icon: "pi pi-key",
          command: () => emit("setPassword"),
        },
        {
          label: "Invite to Mobile App",
          icon: "pi pi-send",
          command: handleInviteToMobileApp,
          disabled: isInviting.value,
        },
      ]
    : []),
]);

const dangerousAction = computed(() => ({
  label: "Archive",
  icon: "pi pi-trash",
  command: () => emit("archive"),
  dangerous: true as const,
}));
</script>
